import { useCallback, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { MemberRoleInput, Role } from '@ready/shared';
import { useApi, type UserProfile } from '@/lib/api';
import { useMe } from '@/lib/me';
import { formatDate } from '@/lib/dates';
import { ROLE_LABELS } from '@/lib/labels';
import { Button, Card, ErrorText, Field, Select } from '@/components/ui';
import { ConfirmDialog } from '@/components/confirm-dialog';
import { UserProfileView } from '@/components/user-profile';
import { useOrgContext } from './layout';

/**
 * A member's profile for the organization's admins: their roles here, and the
 * courses, quiz results and certificates from their time in this organization
 * and the ones under it. The org's own admins can change their role here or
 * remove them; parent admins only look.
 */
export function OrgMemberProfilePage() {
  const { org } = useOrgContext();
  const { userId = '' } = useParams();
  const api = useApi();
  const profile = useQuery({
    queryKey: ['memberProfile', org.id, userId],
    queryFn: () => api.memberProfile(org.id, userId),
  });
  if (profile.isPending) return <p className="text-sm text-muted-foreground">Loading…</p>;
  if (profile.isError) return <ErrorText error={profile.error} />;
  return (
    <UserProfileView
      profile={profile.data}
      back={{ to: `/organizations/${org.id}/members`, label: 'Members' }}
      scopeNote={`Activity in ${org.name} and the organizations under it, from when they joined. Their other learning stays private.`}
      actions={<MemberActions profile={profile.data} />}
    />
  );
}

/** The roles admins hand out here; judge and facilitator stay listed for people who have them. */
const ROLE_CHOICES = ['learner', 'author', 'admin'] as const;

const ROLE_HINTS: Partial<Record<Role, string>> = {
  learner: 'takes courses, assessments and live sessions',
  author: 'also creates courses, quizzes and workshops for the organization',
  admin: 'also manages members, invitations, content and reports',
};

function MemberActions({ profile: p }: { profile: UserProfile }) {
  const { org, role: myRole, canManage } = useOrgContext();
  const me = useMe();
  const api = useApi();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const membership = p.memberships.find((m) => m.orgId === org.id && !m.leftAt);
  const [role, setRole] = useState<Role | null>(null);
  const [confirm, setConfirm] = useState<'role' | 'remove' | null>(null);
  const close = useCallback(() => setConfirm(null), []);
  const name = p.user.displayName ?? p.user.email;

  const refresh = () =>
    Promise.all(
      [
        ['memberProfile', org.id],
        ['members', org.id],
        ['memberSummary', org.id],
      ].map((queryKey) => queryClient.invalidateQueries({ queryKey })),
    );
  const changeRole = useMutation({
    mutationFn: (to: Role) =>
      api.changeMemberRole(org.id, p.user.id, to as MemberRoleInput['role']),
    onSuccess: async () => {
      setConfirm(null);
      setRole(null);
      await refresh();
    },
  });
  const remove = useMutation({
    mutationFn: () => api.removeMember(org.id, p.user.id),
    onSuccess: async () => {
      await refresh();
      queryClient.invalidateQueries({ queryKey: ['usage', org.id] });
      navigate(`/organizations/${org.id}/members`);
    },
  });

  // Parent admins and people no longer in this org (only in an org under it) get no controls.
  if (!canManage || !membership) return null;
  const current = membership.role;
  const blocked =
    p.user.id === me.data?.id
      ? `You can't change your own role or remove yourself. Another ${current === 'owner' ? 'owner' : 'admin or owner'} can.`
      : current === 'owner' && myRole !== 'owner'
        ? 'Only an owner can change or remove an owner.'
        : null;
  if (blocked)
    return (
      <Card title={`In ${org.name}`}>
        <p className="text-sm">
          {ROLE_LABELS[current]} · joined {formatDate(membership.joinedAt)}
        </p>
        <p className="mt-1 text-sm text-muted-foreground">{blocked}</p>
      </Card>
    );

  const selected = role ?? current;
  const choices: Role[] = ROLE_CHOICES.includes(current as (typeof ROLE_CHOICES)[number])
    ? [...ROLE_CHOICES]
    : [current, ...ROLE_CHOICES];

  return (
    <Card title={`In ${org.name}`}>
      <form
        className="max-w-sm space-y-3"
        onSubmit={(e) => {
          e.preventDefault();
          if (selected !== current) setConfirm('role');
        }}
      >
        <Field
          label="Role"
          htmlFor="member-role"
          hint={
            ROLE_HINTS[selected] ? `${ROLE_LABELS[selected]}: ${ROLE_HINTS[selected]}.` : undefined
          }
        >
          <Select
            id="member-role"
            value={selected}
            onChange={(e) => setRole(e.target.value as Role)}
          >
            {choices.map((r) => (
              <option key={r} value={r} disabled={r === 'owner'}>
                {ROLE_LABELS[r]}
              </option>
            ))}
          </Select>
        </Field>
        <Button type="submit" className="w-full sm:w-auto" disabled={selected === current}>
          Change role
        </Button>
      </form>
      <div className="mt-4 flex flex-col gap-3 border-t border-border pt-4 sm:flex-row sm:items-center sm:justify-between">
        <p className="text-sm text-muted-foreground">
          Removing {name} ends their access to {org.name}. Their certificates and learning stay
          theirs.
        </p>
        <Button
          variant="secondary"
          className="w-full shrink-0 sm:w-auto"
          onClick={() => setConfirm('remove')}
        >
          Remove from {org.name}
        </Button>
      </div>

      {confirm === 'role' && (
        <ConfirmDialog
          title={`Make ${name} ${selected === 'admin' || selected === 'author' ? 'an' : 'a'} ${ROLE_LABELS[selected].toLowerCase()}?`}
          confirmLabel="Change role"
          busy={changeRole.isPending}
          error={changeRole.error}
          onConfirm={() => changeRole.mutate(selected)}
          onClose={close}
        >
          <p>
            {ROLE_LABELS[current]} → {ROLE_LABELS[selected]} in {org.name}
            {ROLE_HINTS[selected]
              ? `: a ${ROLE_LABELS[selected].toLowerCase()} ${ROLE_HINTS[selected]}.`
              : '.'}
          </p>
          {current === 'owner' && <p>They will no longer be an owner of {org.name}.</p>}
        </ConfirmDialog>
      )}
      {confirm === 'remove' && (
        <ConfirmDialog
          title={`Remove ${name} from ${org.name}?`}
          confirmLabel="Remove"
          danger
          busy={remove.isPending}
          error={remove.error}
          onConfirm={() => remove.mutate()}
          onClose={close}
        >
          <p>
            They lose access to {org.name}&rsquo;s members-only courses, events and assignments, and
            leave its groups.
          </p>
          <p>
            Their certificates, course progress and quiz results stay with them, and this
            organization&rsquo;s reports keep what they did while they were here. You can invite
            them again later.
          </p>
        </ConfirmDialog>
      )}
    </Card>
  );
}
